import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Typography, Box, Stack, Skeleton } from '@mui/material';
import User, { Company, Contact, Service } from '../../../../interfaces/users.interface';
import { DataCell, DataRow } from '../../../Tables/DataList';
import { tightTableSx } from '../../../Tables/dataTableStyles';
import { useAppSelector } from '../../../../app/hooks';
import SiteRowWithWorkflow from './SiteRowWithWorkflow';
import SiteSidebar from './SiteSidebar';
import LocationForm from '../LocationForm';
import StandardDialog from '../../../Modals/StandardDialog';
import { useCompanySites } from './useCompanySites';
import { useCompanyResource } from '../../../Vendors/useCompanyResource';
import { DEFAULT_PER_PAGE, TabSearchField, TabSelectFilter, TabSelectFilterOption } from '../../../Vendors/companyTabUi';
import { useSectionPager } from '../../../Vendors/sectionPager';
import { getCompanySites, getCompanyServices, getCompanyUsers } from '../../../../services/companyDetail';
import { assessmentSteps } from '../../../../services/surveyHelpers';
import { siteIdsWithWorkflowStatus } from './siteWorkflowDisplay';

// Matches a loaded row, whose height comes from the 55px shield and the stacked action buttons.
const SKELETON_ROW_HEIGHT = 76;

// The option labels are the status strings the column renders.
export const SITE_STATUS_FILTER_OPTIONS: TabSelectFilterOption[] = assessmentSteps.map((step) => ({ value: step, label: step }));

const ALL = 9999;

type SitesTabProps = {
  company: Company;
  companyCallback: any;
  userCanAdminCompany: boolean;
  createSiteOpen?: boolean;
  onCreateSiteOpen?: () => void;
  onCreateSiteClose?: () => void;
};

export default function SitesTab(props: SitesTabProps) {
  const { company, companyCallback, userCanAdminCompany, createSiteOpen: externalCreateSiteOpen, onCreateSiteClose } = props;
  const { workflows: fullWf, workflowsLoaded, workflowsFailed } = useAppSelector((state) => state.workflow);

  const [internalCreateSiteOpen, setInternalCreateSiteOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [canStartNewQuestionniare, setCanStartNewQuestionniare] = useState(false);
  const [selectedSite, setSelectedSite] = useState<Contact | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PER_PAGE);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Debounce typing into the server-side ?search= so each keystroke doesn't fetch.
  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  const handleStatusFilterChange = useCallback((value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
  }, []);

  // Resolved from the workflow store; undefined = no filter, '' = filtering but nothing matched.
  const statusSiteIds = useMemo(
    () => (statusFilter ? siteIdsWithWorkflowStatus(fullWf || [], statusFilter).join(',') : undefined),
    [statusFilter, fullWf],
  );

  // Filtering before the store arrives would resolve no ids and read as an empty company.
  const canFilterByStatus = workflowsLoaded && !workflowsFailed;
  const rowStatusFilter = statusFilter || undefined;

  // Use external dialog state if provided, otherwise fall back to internal state.
  const createSiteOpen = externalCreateSiteOpen !== undefined ? externalCreateSiteOpen : internalCreateSiteOpen;

  const { sites, count, loading, refetch } = useCompanySites(company.id, currentPage, pageSize, search, statusSiteIds);
  const totalPages = Math.max(1, Math.ceil(count / pageSize));
  useSectionPager({ count, page: currentPage, pageSize, setPage: setCurrentPage, setPageSize });

  // Site rows/sidebar read each site's embedded app names + certifications from
  // the sites endpoint itself, so no company-wide apps/certs fetch is needed. The
  // full site list only feeds the add form and the sidebar's edit/delete, so
  // defer it until one of those is open.
  // The add/edit site form's selectors need the full site list, the company's
  // employees (primary contact), and its services; fetch all only when the
  // form/sidebar is open.
  const formDataEnabled = createSiteOpen || isSidebarOpen;
  const { items: allSites } = useCompanyResource<Contact>(getCompanySites, company.id, 1, ALL, formDataEnabled);
  const { items: employees } = useCompanyResource<User>(getCompanyUsers, company.id, 1, ALL, formDataEnabled);
  const { items: services } = useCompanyResource<Service>(getCompanyServices, company.id, 1, ALL, formDataEnabled);
  const cardCompany = useMemo(
    () => ({ ...company, locations: allSites, employees, services }),
    [company, allSites, employees, services],
  );

  // Clamp to the last available page if rows were removed (e.g. after a delete).
  useEffect(() => {
    if (!loading && currentPage > totalPages) setCurrentPage(totalPages);
  }, [loading, currentPage, totalPages]);

  const handleCreateSiteClose = useCallback(() => {
    if (onCreateSiteClose) {
      onCreateSiteClose();
    } else {
      setInternalCreateSiteOpen(false);
    }
  }, [onCreateSiteClose]);

  const handleSetCanStartNewQuestionniare = useCallback(() => {
    setCanStartNewQuestionniare(true);
  }, []);

  const handleRowClick = useCallback((site: Contact) => {
    setSelectedSite(site);
    setIsSidebarOpen(true);
  }, []);

  const handleSidebarClose = useCallback(() => {
    setIsSidebarOpen(false);
    setSelectedSite(null);
  }, []);

  const handleSiteCreation = useCallback(
    (companyResponse: Company) => {
      companyCallback(companyResponse);
      refetch();
      handleCreateSiteClose();
    },
    [companyCallback, refetch, handleCreateSiteClose],
  );

  const handleSuccessfulSiteEdit = useCallback((companyResponse: Company, updateType: 'edit' | 'delete') => {
    companyCallback(companyResponse);
    refetch();
    const currentSite = companyResponse.locations?.find((site) => site.id === selectedSite?.id);
    if (currentSite) {
      setSelectedSite(currentSite);
    }
    if (updateType === 'delete') {
      handleSidebarClose();
    }
  }, [companyCallback, refetch, handleSidebarClose, selectedSite?.id]);

  return (
    <>
      <Stack spacing={2} sx={{ p: 2 }}>
        <StandardDialog title='Add New Site' handleClose={handleCreateSiteClose} isOpen={createSiteOpen}>
          <LocationForm
            submitButtonText='Add Site'
            company={cardCompany}
            successCallback={handleSiteCreation}
            modalClose={() => handleCreateSiteClose()}
          />
        </StandardDialog>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 1, mb: 1 }}>
          <TabSearchField value={searchInput} onChange={setSearchInput} placeholder='Search sites' />
          <TabSelectFilter
            value={statusFilter}
            onChange={handleStatusFilterChange}
            options={SITE_STATUS_FILTER_OPTIONS}
            emptyLabel='All statuses'
            ariaLabel='Filter sites by status'
            disabled={!canFilterByStatus}
          />
        </Box>

        <Box sx={tightTableSx}>
          <DataRow header sx={{ flexWrap: 'nowrap' }}>
            <DataCell xs={0.75} minWidth={90} textAlign='center'>
              Shield
            </DataCell>
            <DataCell xs={2}>Name</DataCell>
            <DataCell xs={2}>Applications</DataCell>
            <DataCell xs={2}>Services</DataCell>
            <DataCell xs={3}>Status</DataCell>
            <DataCell xs={1.75} minWidth={200} textAlign='center'>
              Actions
            </DataCell>
            <DataCell xs={0.5}>&nbsp;</DataCell>
          </DataRow>

          {loading &&
            Array.from({ length: pageSize }).map((_, index) => (
              <DataRow key={`skeleton-${index}`} sx={{ flexWrap: 'nowrap', minHeight: SKELETON_ROW_HEIGHT }}>
                <DataCell xs={0.75} minWidth={90} textAlign='center'><Skeleton variant='circular' width={44} height={44} sx={{ mx: 'auto' }} /></DataCell>
                <DataCell xs={2}><Skeleton variant='text' width='70%' /></DataCell>
                <DataCell xs={2}><Skeleton variant='text' width='30%' /></DataCell>
                <DataCell xs={2}><Skeleton variant='rounded' width={72} height={22} /></DataCell>
                <DataCell xs={3}>
                  <Skeleton variant='text' width='35%' />
                  <Skeleton variant='text' width='75%' />
                </DataCell>
                <DataCell xs={1.75} minWidth={200}>
                  <Stack spacing={0.75} sx={{ width: '100%' }}>
                    <Skeleton variant='rounded' height={26} />
                    <Skeleton variant='rounded' height={26} />
                  </Stack>
                </DataCell>
                <DataCell xs={0.5}>&nbsp;</DataCell>
              </DataRow>
            ))}

          {!loading && sites.length === 0 && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant='body2' color='text.secondary'>
                {search ? `No sites match “${search}”` : statusFilter ? 'No sites with this status' : 'No sites'}
              </Typography>
            </Box>
          )}

          {!loading &&
            sites.map((site: Contact) => (
              <SiteRowWithWorkflow
                key={site.id}
                company={cardCompany}
                site={site}
                fullWf={fullWf}
                userCanAdminCompany={userCanAdminCompany}
                companyCallback={companyCallback}
                onRowClick={handleRowClick}
                handleSetCanStartNewQuestionniare={handleSetCanStartNewQuestionniare}
                statusFilter={rowStatusFilter}
              />
            ))}
        </Box>

      </Stack>

      <SiteSidebar
        canStartNewQuestionniare={canStartNewQuestionniare}
        isOpen={isSidebarOpen}
        company={cardCompany}
        site={selectedSite}
        onClose={handleSidebarClose}
        companyCallback={handleSuccessfulSiteEdit}
        statusFilter={rowStatusFilter}
      />
    </>
  );
}
